import { memo, useCallback, useMemo } from "react";
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { DomainPicker } from "@/components/gsc/DomainPicker";
import { formatGscChartTick, formatGscCount } from "@/lib/gsc-display";
import {
  buildGscTrendChartModel,
  getChartSeriesColor,
  toSafeSeriesKey,
  type GscTrendChartRow,
  type GscTrendMetric,
} from "@/lib/gsc-trend";
import { cn } from "@/lib/utils";
import {
  GSC_TREND_GRANULARITIES,
  type GscTrendGranularity,
  type GscTrendPoint,
} from "@/types/gsc";

const GRANULARITY_LABELS: Record<GscTrendGranularity, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
};

const METRIC_OPTIONS: { value: GscTrendMetric; label: string }[] = [
  { value: "clicks", label: "Clicks" },
  { value: "impressions", label: "Impressions" },
];

interface SegmentedToggleProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}

const SegmentedToggle = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: SegmentedToggleProps<T>) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={cn(
      "inline-flex shrink-0 rounded-lg border border-border bg-muted/40 p-0.5",
      className,
    )}
  >
    {options.map((option) => {
      const isActive = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer",
            isActive
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
          aria-pressed={isActive}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

interface TrendChartProps {
  points: GscTrendPoint[];
  metric: GscTrendMetric;
  granularity: GscTrendGranularity;
  selectedDomains: string[] | null;
  availableDomains: string[];
  isLoading?: boolean;
  isFetching?: boolean;
  onMetricChange: (metric: GscTrendMetric) => void;
  onGranularityChange: (granularity: GscTrendGranularity) => void;
  onSelectedDomainsChange: (domains: string[]) => void;
  className?: string;
}

const TrendChartSkeleton = () => (
  <Card className="border-border shadow-none">
    <CardHeader className="space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-44" />
      </div>
      <Skeleton className="h-7 w-full max-w-2xl" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-[360px] w-full" />
    </CardContent>
  </Card>
);

export const TrendChart = memo(
  ({
    points,
    metric,
    granularity,
    selectedDomains,
    availableDomains,
    isLoading = false,
    isFetching = false,
    onMetricChange,
    onGranularityChange,
    onSelectedDomainsChange,
    className,
  }: TrendChartProps) => {
    const urlFilteredDomains = selectedDomains ?? [];

    const { rows, series } = useMemo(
      () =>
        buildGscTrendChartModel(points, granularity, selectedDomains, metric),
      [points, granularity, selectedDomains, metric],
    );

    const expectedDomains = useMemo(
      (): readonly string[] =>
        urlFilteredDomains.length > 0
          ? urlFilteredDomains
          : Array.from(new Set(series.map((item) => item.domain))),
      [urlFilteredDomains, series],
    );

    const expectedSeries = useMemo(
      () =>
        expectedDomains.map((domain, index) => {
          const catalogIndex = availableDomains.indexOf(domain);
          return {
            key: toSafeSeriesKey(domain, index),
            domain,
            color: getChartSeriesColor(
              catalogIndex >= 0 ? catalogIndex : index,
            ),
          };
        }),
      [availableDomains, expectedDomains],
    );

    const legendItems = useMemo(
      () =>
        expectedSeries.map((item) => ({
          domain: item.domain,
          color: item.color,
        })),
      [expectedSeries],
    );

    const chartConfig = useMemo(() => {
      const config: ChartConfig = {};
      for (const item of expectedSeries) {
        config[item.key] = {
          label: item.domain,
          color: item.color,
        };
      }
      return config;
    }, [expectedSeries]);

    // Ghost domains (0 traffic) never appear in buildGscTrendChartModel rows —
    // pad every expected series key so Recharts includes them in the tooltip.
    const paddedRows = useMemo((): GscTrendChartRow[] => {
      const keys = expectedSeries.map((item) => item.key);
      return rows.map((row) => {
        const next: GscTrendChartRow = { ...row };
        for (const key of keys) {
          if (next[key] == null) {
            next[key] = 0;
          }
        }
        return next;
      });
    }, [rows, expectedSeries]);

    const handleRemoveDomain = useCallback(
      (domain: string) => {
        if (expectedDomains.length <= 1) return;
        onSelectedDomainsChange(
          expectedDomains.filter((value) => value !== domain),
        );
      },
      [expectedDomains, onSelectedDomainsChange],
    );

    const handleAddDomain = useCallback(
      (domain: string) => {
        onSelectedDomainsChange(
          Array.from(new Set([...expectedDomains, domain])),
        );
      },
      [expectedDomains, onSelectedDomainsChange],
    );

    if (isLoading) {
      return (
        <div
          className={className}
          aria-busy
          aria-label="Loading GSC trend chart"
        >
          <TrendChartSkeleton />
        </div>
      );
    }

    const hasData = rows.length > 0 && series.length > 0;

    return (
      <Card
        className={cn(
          "border-border shadow-none",
          isFetching && "pointer-events-none opacity-60",
          className,
        )}
        aria-busy={isFetching}
        aria-label="GSC traffic trends"
      >
        <CardHeader className="space-y-3 pb-2">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <CardTitle className="text-base font-semibold tracking-tight">
                Traffic Trends
              </CardTitle>

              <SegmentedToggle
                value={metric}
                options={METRIC_OPTIONS}
                onChange={onMetricChange}
                ariaLabel="Trend metric"
              />

              <SegmentedToggle
                value={granularity}
                options={GSC_TREND_GRANULARITIES.map((value) => ({
                  value,
                  label: GRANULARITY_LABELS[value],
                }))}
                onChange={onGranularityChange}
                ariaLabel="Trend granularity"
              />
            </div>

            {expectedDomains.length > 0 || availableDomains.length > 0 ? (
              <DomainPicker
                items={legendItems}
                availableDomains={availableDomains}
                onRemoveDomain={handleRemoveDomain}
                onAddDomain={handleAddDomain}
              />
            ) : null}
          </div>
        </CardHeader>

        <CardContent className="pl-2">
          {hasData ? (
            <div className="h-[360px] w-full">
              <ChartContainer config={chartConfig} className="h-full w-full">
                <LineChart
                  data={paddedRows}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical />
                  <XAxis
                    dataKey="dateLabel"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={28}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    width={44}
                    tickFormatter={(value) => formatGscChartTick(Number(value))}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        indicator="line"
                        formatter={(value, name) => {
                          const config = chartConfig[String(name)];
                          return (
                            <div className="flex w-full items-center justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className="size-2 shrink-0 rounded-full"
                                  style={{
                                    backgroundColor:
                                      config?.color ??
                                      "var(--muted-foreground)",
                                  }}
                                  aria-hidden
                                />
                                <span className="truncate text-muted-foreground">
                                  {config?.label ?? name}
                                </span>
                              </div>
                              <span className="font-mono font-medium tabular-nums text-foreground">
                                {formatGscCount(Number(value))}
                              </span>
                            </div>
                          );
                        }}
                      />
                    }
                  />
                  {expectedSeries.map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.key}
                      stroke={`var(--color-${item.key})`}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ChartContainer>
            </div>
          ) : (
            <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-6 text-center text-sm text-muted-foreground">
              No trend data for the selected period and filters.
            </div>
          )}
        </CardContent>
      </Card>
    );
  },
);

TrendChart.displayName = "TrendChart";
